import { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { VoiceMicButton } from './VoiceMicButton';
import { Button, ProgressBar, Textarea, Toast } from './ui';

interface QuestionStepProps {
  step: number;
  total: number;
  question: { title: string; description: string | null };
  value: string;
  onChange: (next: string) => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  saveError?: string | null;
  nextLabel?: string;
  onExit?: () => void;
  sessionId: number;
}

const MAX_LEN = 2000;
const MAX_RECORD_MS = 60_000;
const TOAST_TIMEOUT_MS = 3000;
const TRANSCRIBE_FAILURE_TEXT = 'Не удалось распознать. Попробуйте ещё раз';

function filenameFor(mime: string): string {
  if (mime.includes('mp4')) return 'audio.mp4';
  return 'audio.webm';
}

export function QuestionStep({
  step,
  total,
  question,
  value,
  onChange,
  onNext,
  onBack,
  saving,
  saveError,
  nextLabel,
  onExit,
  sessionId,
}: QuestionStepProps) {
  const trimmed = value.trim();
  const canNext = trimmed.length >= 1 && trimmed.length <= MAX_LEN;
  const isLast = step === total;
  const progressPct = total === 0 ? 0 : Math.round((step / total) * 100);

  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => {
      setToast((current) => (current === msg ? null : current));
    }, TOAST_TIMEOUT_MS);
  };

  const recorder = useVoiceRecorder({
    maxDurationMs: MAX_RECORD_MS,
    minDurationMs: 300,
    onResult: async (blob, mimeType) => {
      try {
        const { text } = await api.transcribeSession(sessionId, blob, filenameFor(mimeType));
        const trimmedText = text.trim();
        if (trimmedText.length === 0) {
          showToast(TRANSCRIBE_FAILURE_TEXT);
          return;
        }
        const next = value.trim() ? `${value} ${trimmedText}` : trimmedText;
        const capped = next.length > MAX_LEN ? next.slice(0, MAX_LEN) : next;
        onChange(capped);
      } catch (err) {
        if (err instanceof ApiError) {
          // no-op; classified for future per-code handling
        }
        showToast(TRANSCRIBE_FAILURE_TEXT);
      }
    },
    onError: () => {
      showToast(TRANSCRIBE_FAILURE_TEXT);
    },
  });

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-8 flex flex-col safe-top">
      <header className="mb-4">
        <div className="flex items-center justify-between text-xs text-tg-hint mb-2">
          <span className="font-medium tabular-nums">
            Шаг {step} из {total} · {progressPct}%
          </span>
          {onExit && (
            <button
              type="button"
              onClick={onExit}
              className="text-tg-hint hover:text-tg-text transition-colors"
            >
              На главную
            </button>
          )}
        </div>
        <ProgressBar value={progressPct} ariaLabel={`Прогресс: шаг ${step} из ${total}`} />
      </header>

      <div className="mb-3">
        <h2 className="text-lg font-semibold leading-snug">{question.title}</h2>
        {question.description && (
          <p className="text-sm text-tg-hint mt-1 leading-relaxed whitespace-pre-wrap">
            {question.description}
          </p>
        )}
      </div>

      {toast && (
        <Toast tone="destructive" className="mb-2">
          {toast}
        </Toast>
      )}

      <div className="relative flex-1 min-h-[160px] flex flex-col">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Ваш ответ..."
          maxLength={MAX_LEN}
          rows={6}
          autoFocus
          className="pr-14 flex-1 min-h-[160px]"
        />
        <VoiceMicButton
          state={recorder.state}
          remainingMs={recorder.remainingMs}
          onPressStart={() => void recorder.start()}
          onPressEnd={recorder.stop}
          className="absolute right-2 bottom-2"
        />
      </div>
      <div className="text-xs text-tg-hint text-right mt-1 tabular-nums">
        {value.length}/{MAX_LEN}
      </div>

      {saveError && (
        <Toast tone="destructive" className="mt-2">
          {saveError}
        </Toast>
      )}

      <div className="flex gap-2 mt-4">
        <Button
          variant="secondary"
          size="lg"
          onClick={onBack}
          disabled={saving}
          fullWidth
          leftIcon={<ArrowLeft size={18} />}
          hapticKind="light"
        >
          Назад
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          disabled={!canNext || saving}
          loading={saving}
          fullWidth
          rightIcon={!saving ? <ArrowRight size={18} /> : undefined}
          hapticKind="medium"
        >
          {saving ? 'Сохранение' : (nextLabel ?? (isLast ? 'Завершить' : 'Дальше'))}
        </Button>
      </div>
    </div>
  );
}
